<template>
  <header :class="['header-wrapper', { scrolled: isScrolled }]">
    <div class="container">
      <!-- Logo -->
      <div class="logo">
        <router-link to="/">
          <img src="@/assets/home/logo.png"
               alt="ZETOK - 返回首页"
               class="logo-image" />
        </router-link>
      </div>

      <!-- Navigation -->
      <nav class="nav" aria-label="主导航">
        <ul>
          <li v-for="item in navItems"
              :key="item.label"
              class="nav-item"
              @mouseenter="openSub(item)"
              @mouseleave="closeSub(item, $event)">
            <template v-if="hasSubmenu(item)">
              <span class="nav-label"
                    aria-haspopup="true"
                    :aria-expanded="item.open ? 'true' : 'false'"
                    @click="toggleMobileSub(item)">{{ item.label }}</span>
            </template>
            <template v-else>
              <router-link :to="item.path"
                           active-class="active"
                           exact>{{ item.label }}</router-link>
            </template>
            <div v-if="hasSubmenu(item)"
                 :class="['submenu', { open: item.open }]"
                 @mouseenter="openSub(item)"
                 @mouseleave="closeSub(item, $event)">
              <div class="submenu-inner"
                   :class="(item.groups && item.groups.length) ? 'submenu-products' : 'submenu-normal'">
                <!-- 三列分组（产品） -->
                <template v-if="item.groups && item.groups.length">
                  <div class="submenu-columns">
                    <div v-for="(group, gi) in item.groups"
                         :key="'grp-' + gi"
                         class="submenu-col">
                      <div class="submenu-title">{{ group.title }}</div>
                      <ul class="submenu-list">
                        <li v-for="link in group.items"
                            :key="link.label"
                            class="submenu-link">
                          <router-link :to="link.path"
                                       @click.native="onSubmenuClick">{{ link.label }}</router-link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </template>
                <!-- 普通列表（其他） -->
                <template v-else>
                  <ul class="submenu-list-vertical">
                    <li v-for="child in item.children"
                        :key="child.label"
                        class="submenu-item">
                      <router-link :to="child.path"
                                   @click.native="onSubmenuClick">{{ child.label }}</router-link>
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </li>
        </ul>
      </nav>

      <div class="header-actions">
        <button class="cart-button" @click="goToCart" aria-label="商店">
          <svg 
            class="cart-icon" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id="cartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:#4285f4;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#1a5db0;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path d="M3 2C2.44772 2 2 2.44772 2 3C2 3.55228 2.44772 4 3 4H4.5L6.5 13.5L5.5 15.5C4.5 17.5 6 19 8 19H19C20.5 19 21.5 18 21.5 16.5C21.5 15.5 21 14.5 20 14H8L9 11H20C21 11 22 10 22 9L20 5H7L6 2H3Z" fill="url(#cartGradient)"/>
            <circle cx="8" cy="20" r="2" fill="url(#cartGradient)"/>
            <circle cx="18" cy="20" r="2" fill="url(#cartGradient)"/>
          </svg>
          <span>{{ $t('nav.store') || '商店' }}</span>
        </button>
        <!-- Language Switcher -->
        <LanguageSwitcher />
        
        <!-- ✅ 移动端汉堡菜单按钮 -->
        <button class="hamburger-btn" @click="toggleMobileMenu" aria-label="菜单">
          <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
          <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
          <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
        </button>
      </div>
    </div>
    
    <!-- ✅ 移动端下拉菜单 - 修复点击问题 -->
    <div class="mobile-menu" :class="{ open: mobileMenuOpen }">
      <ul class="mobile-nav-list">
        <li v-for="item in navItems" :key="item.label" class="mobile-nav-item">
          <template v-if="hasSubmenu(item)">
            <div class="mobile-nav-label" @click.stop="toggleMobileSub(item)">
              {{ item.label }}
              <span class="mobile-arrow" :class="{ open: item.open }">›</span>
            </div>
            <div class="mobile-submenu" :class="{ open: item.open }">
              <!-- 分组产品 -->
              <template v-if="item.groups && item.groups.length">
                <div v-for="group in item.groups" :key="group.title" class="mobile-group">
                  <div class="mobile-group-title">{{ group.title }}</div>
                  <router-link 
                    v-for="link in group.items" 
                    :key="link.label"
                    :to="link.path"
                    @click.native="closeMobileMenu"
                    class="mobile-sub-link"
                  >{{ link.label }}</router-link>
                </div>
              </template>
              <!-- 普通子菜单 -->
              <template v-else>
                <router-link 
                  v-for="child in item.children" 
                  :key="child.label"
                  :to="child.path"
                  @click.native="closeMobileMenu"
                  class="mobile-sub-link"
                >{{ child.label }}</router-link>
              </template>
            </div>
          </template>
          <template v-else>
            <router-link :to="item.path" @click.native="closeMobileMenu" class="mobile-nav-link">{{ item.label }}</router-link>
          </template>
        </li>
      </ul>
    </div>
  </header>
</template>

<script>
import LanguageSwitcher from './LanguageSwitcher.vue'

export default {
  name: 'HeaderBar',
  components: { LanguageSwitcher },
  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      navItems: [],
    }
  },
  computed: {
    locale() {
      return this.$i18n && this.$i18n.locale
    },
    // ✅ 判断是否手机端
    isMobile() {
      return window.innerWidth <= 768
    },
  },
  methods: {
    hasSubmenu(item) {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0
      const hasGroups = Array.isArray(item.groups) && item.groups.length > 0
      return hasChildren || hasGroups
    },
    buildNavItems() {
      this.navItems = [
        {
          label: this.$t('nav.products') || '产品',
          path: '/products',
          groups: [
            {
              title: this.$t('home.products.tabs.kits') || '套件',
              items: [
                { label: this.$t('nav.smartFarm') || '智能农场', path: '/products/farm' },
                { label: this.$t('nav.smartHome') || '智能家居', path: '/products/home' },
                { label: this.$t('nav.smartTraffic') || '智能交通', path: '/products/traffic' },
                { label: this.$t('nav.smartFactory') || '智能工厂', path: '/products/factory' },
                { label: this.$t('nav.laserSteam') || '激光切割 STEAM 套件', path: '/products/laser-steam' },
                { label: this.$t('nav.dreamScience') || '逐梦科学套件', path: '/products/dream-science' },
                { label: this.$t('nav.viewMore') || '查看更多', path: '/products/kits' },
              ],
            },
            {
              title: this.$t('nav.device') || '设备',
              items: [
                { label: this.$t('home.products.tabs.laser') || '激光设备', path: '/products/laser' },
              ],
            },
            {
              title: this.$t('home.products.tabs.courses') || '课程',
              items: [
                { label: this.$t('nav.coursesSeries') || '四大课程系列', path: '/products/course' },
              ],
            },
          ],
          open: false,
        },
        {
          label: this.$t('nav.education') || '教育方案',
          path: '/solutions',
          children: [
            { label: this.$t('nav.stem') || 'STEM教育解决方案', path: '/solutions/stem' },
            { label: this.$t('nav.maker') || '创客空间解决方案', path: '/solutions/maker-space' },
          ],
          open: false,
        },
        { label: this.$t('nav.software') || '软件', path: '/software', open: false },
        {
          label: this.$t('nav.service') || '服务与支持',
          path: '/support',
          children: [
            { label: this.$t('nav.afterSale') || '教程下载', path: '/support/after-sale' },
          ],
          open: false,
        },
        {
          label: this.$t('nav.about') || '关于我们',
          path: '/about',
          children: [
            { label: this.$t('nav.aboutUs') || '关于我们', path: '/about' },
            { label: this.$t('nav.dealer') || '成为我们的经销商', path: '/about/dealer' },
            { label: this.$t('nav.cooperation') || '产品使用与教育合作', path: '/about/cooperation' },
            { label: this.$t('nav.news') || '新闻', path: '/about/news' },
          ],
          open: false,
        },
      ]
    },
    goToCart() {
      // window.open('https://website.zz-zetok.com/', '_blank')
      return
    },
    onSubmenuClick() {
      try {
        window.scrollTo({ top: 0, behavior: 'auto' })
      } catch (e) {
        window.scrollTo(0, 0)
      }
      this.closeAll()
      this.closeMobileMenu()
    },
    openSub(item) {
      if (!this.hasSubmenu(item) || this.isMobile) return
      item.open = true
      if (!this._closeTimers) this._closeTimers = {}
      const key = item.label
      if (this._closeTimers[key]) {
        clearTimeout(this._closeTimers[key])
        delete this._closeTimers[key]
      }
    },
    closeSub(item, e) {
      if (!this.hasSubmenu(item) || this.isMobile) return
      const rt = e && e.relatedTarget
      const isToSubmenu = rt && typeof rt.closest === 'function' && rt.closest('.submenu')
      const isToNavItem = rt && typeof rt.closest === 'function' && rt.closest('.nav-item')
      if (isToSubmenu || isToNavItem) return
      if (!this._closeTimers) this._closeTimers = {}
      const key = item.label
      if (this._closeTimers[key]) clearTimeout(this._closeTimers[key])
      this._closeTimers[key] = setTimeout(() => {
        item.open = false
        delete this._closeTimers[key]
      }, 160)
    },
    // ✅ 移动端子菜单切换 - 移除 isMobile 判断，使用 $set 确保响应式
    toggleMobileSub(item) {
      // 移除 if (!this.isMobile) return 限制
      // 使用 $set 确保 Vue 能检测到变化
      this.$set(item, 'open', !item.open)
    },
    // ✅ 切换移动端菜单
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
      if (this.mobileMenuOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        this.closeAll()
      }
    },
    // ✅ 关闭移动端菜单
    closeMobileMenu() {
      this.mobileMenuOpen = false
      document.body.style.overflow = ''
      this.closeAll()
    },
    closeAll() {
      this.navItems.forEach(it => (it.open = false))
    },
    // ✅ 监听窗口变化，自动关闭移动端菜单
    handleResize() {
      // 当窗口宽度大于 768px（电脑端）时，关闭移动端菜单
      if (window.innerWidth > 768 && this.mobileMenuOpen) {
        this.mobileMenuOpen = false
        document.body.style.overflow = ''
        this.closeAll()
      }
    },
    onKeydown(e) {
      if (e && (e.key === 'Escape' || e.key === 'Esc')) {
        this.closeAll()
        this.closeMobileMenu()
      }
      if (e && (e.key === 'Enter' || e.key === ' ') && e.target.closest('.cart-button')) {
        e.preventDefault()
        this.goToCart()
      }
    },
    onScroll() {
      this.isScrolled = window.scrollY > 2
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('scroll', this.onScroll, { passive: true })
    // ✅ 添加 resize 监听，用于自动关闭移动端菜单
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.buildNavItems()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('scroll', this.onScroll)
    // ✅ 移除 resize 监听
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    locale() {
      this.buildNavItems()
    },
  },
}
</script>

<style scoped>
/* ============================================
   基础样式
   ============================================ */
.header-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1100;
  background: #ffffff;
  height: 70px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  transition: box-shadow 0.2s ease, background-color 0.2s ease;
}

.header-wrapper.scrolled {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  height: 70px;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

/* ===== Logo ===== */
.logo {
  margin-right: 40px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.logo a {
  display: flex;
  align-items: center;
  height: 100%;
}

.logo-image {
  display: block;
  height: 40px;
  width: auto;
  max-height: 40px;
  object-fit: contain;
}

/* ===== 导航 ===== */
.nav {
  flex: 1;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  align-items: center;
}

.nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
}

.nav-item {
  position: relative;
  margin-right: 30px;
}

.nav-item > a {
  color: #333;
  text-decoration: none;
  padding-bottom: 4px;
  white-space: nowrap;
  display: inline-block;
}

.nav-item > a:hover {
  color: #4285f4;
}

.nav-item > .nav-label {
  color: #333;
  padding-bottom: 4px;
  white-space: nowrap;
  display: inline-block;
  cursor: default;
}

.nav-item > .nav-label:hover {
  color: #4285f4;
}

/* ===== 右侧操作栏 ===== */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* ===== 购物车按钮 ===== */
.cart-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  border: 1px solid #4887E8;
  border-radius: 20px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  color: #4887E8;
  font-size: 14px;
  font-weight: 500;
  min-height: 36px;
  white-space: nowrap;
  padding: 0px 20px;
}

.cart-button:active {
  transform: scale(0.98);
}

.cart-button:focus-visible {
  outline: 2px solid #4285f4;
  outline-offset: 2px;
}

.cart-icon {
  display: block;
  color: currentColor;
}

/* ===== 汉堡菜单按钮 ===== */
.hamburger-btn {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
}

.hamburger-line {
  display: block;
  width: 100%;
  height: 2px;
  background: #333;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger-line.active:nth-child(1) {
  transform: rotate(45deg) translate(5px, 6px);
}

.hamburger-line.active:nth-child(2) {
  opacity: 0;
}

.hamburger-line.active:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -6px);
}

/* ===== 子菜单（桌面端） ===== */
.submenu {
  position: fixed;
  left: 0;
  top: 71px;
  width: 100vw;
  background: #fff;
  display: none;
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  z-index: 1000;
  min-height: 220px;
}

.submenu.open {
  display: block;
  opacity: 1;
  transform: translateY(0);
}

.submenu-inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.submenu-inner.submenu-products {
  align-items: flex-start;
  justify-content: center;
}

.submenu-inner.submenu-normal {
  min-height: 220px;
  align-items: center;
  justify-content: center;
}

.submenu a {
  color: #333;
  text-decoration: none;
}

.submenu a:visited {
  color: #333;
  text-decoration: none;
}

.submenu a:hover {
  color: #4285f4;
}

.submenu-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 120px;
  justify-items: center;
  align-items: start;
}

.submenu-col {
  padding: 0 20px;
  border-left: 1px solid #eee;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.submenu-col:first-child {
  border-left: none;
}

.submenu-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  text-align: left;
}

.submenu-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.submenu-link {
  margin: 8px 0;
}

.submenu-link a {
  color: #333;
  text-decoration: none;
}

.submenu-link a:visited {
  color: #333;
  text-decoration: none;
}

.submenu-link a:hover {
  color: #4285f4;
}

.submenu-list-vertical {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.submenu-item {
  flex: none;
  padding: 6px 0;
  white-space: nowrap;
  text-align: center;
}

.submenu-item a {
  color: #333;
  text-decoration: none !important;
}

.submenu-item a:visited {
  color: #333 !important;
  text-decoration: none !important;
}

.submenu-item a:hover {
  color: #4285f4 !important;
}

/* ============================================
   移动端菜单 - 修复点击问题
   ============================================ */
.mobile-menu {
  display: none;
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  padding: 20px;
  overflow-y: auto;
  z-index: 999;
  border-top: 1px solid #f0f0f0;
  -webkit-overflow-scrolling: touch;
}

.mobile-menu.open {
  display: block;
}

.mobile-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-nav-item {
  border-bottom: 1px solid #f5f5f5;
}

/* ✅ 增加点击区域和反馈 */
.mobile-nav-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  min-height: 50px;
}

/* ✅ 点击反馈 */
.mobile-nav-label:active {
  background: rgba(0, 0, 0, 0.05);
}

.mobile-nav-link {
  display: block;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-decoration: none;
  min-height: 50px;
}

.mobile-arrow {
  font-size: 18px;
  color: #999;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.mobile-arrow.open {
  transform: rotate(90deg);
}

.mobile-submenu {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.35s ease;
  padding-left: 12px;
}

.mobile-submenu.open {
  max-height: 1500px;
}

.mobile-group {
  margin-bottom: 12px;
}

.mobile-group-title {
  font-size: 13px;
  color: #999;
  font-weight: 600;
  padding: 8px 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mobile-sub-link {
  display: block;
  padding: 10px 0 10px 8px;
  font-size: 14px;
  color: #555;
  text-decoration: none;
  border-bottom: 1px solid #f8f8f8;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
}

.mobile-sub-link:active {
  background: rgba(0, 0, 0, 0.03);
}

.mobile-sub-link:hover {
  color: #4285f4;
}

/* ============================================
   响应式适配
   ============================================ */

/* ===== 平板端 (1024px) ===== */
@media (max-width: 1024px) {
  .logo {
    margin-right: 20px;
  }
  
  .nav-item {
    margin-right: 18px;
  }

  .submenu-columns {
    gap: 60px;
  }
}

/* ===== 手机端 (<= 768px) ===== */
@media (max-width: 768px) {
  .header-wrapper {
    height: 64px;
  }

  .container {
    height: 64px;
    padding: 0 16px;
  }

  .logo-image {
    height: 32px;
    max-height: 32px;
  }

  .logo {
    margin-right: 12px;
  }

  .nav {
    display: none;
  }

  .hamburger-btn {
    display: flex;
  }

  .mobile-menu {
    top: 64px;
  }

  .cart-button {
    padding: 0 12px;
    min-height: 32px;
    font-size: 13px;
    gap: 4px;
  }

  .cart-button span {
    display: none;
  }

  .cart-icon {
    width: 18px;
    height: 18px;
  }

  .submenu {
    display: none !important;
  }

  .header-actions {
    gap: 8px;
  }
}

/* ===== 小屏手机 (<= 480px) ===== */
@media (max-width: 480px) {
  .header-wrapper {
    height: 56px;
  }

  .container {
    height: 56px;
    padding: 0 12px;
  }

  .logo-image {
    height: 28px;
    max-height: 28px;
  }

  .logo {
    margin-right: 8px;
  }

  .mobile-menu {
    top: 56px;
    padding: 16px;
  }

  .cart-button {
    min-height: 28px;
    padding: 0 10px;
  }

  .cart-icon {
    width: 16px;
    height: 16px;
  }

  .hamburger-btn {
    width: 20px;
    height: 16px;
  }

  .mobile-nav-label,
  .mobile-nav-link {
    font-size: 15px;
    padding: 12px 0;
  }

  .mobile-sub-link {
    font-size: 13px;
    padding: 8px 0 8px 8px;
  }
}
</style>